"use client"

import { useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Home, RefreshCcw, ArrowLeft, AlertTriangle } from "lucide-react"
import { motion } from "framer-motion"
import Link from "next/link"

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.08,
            delayChildren: 0.1,
        },
    },
}

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.4, ease: "easeOut" as const },
    },
}

export default function Error({
    error,
    reset,
}: {
    error: Error & { digest?: string }
    reset: () => void
}) {
    useEffect(() => {
        console.error(error)
    }, [error])

    return (
        <div className="min-h-[80vh] flex items-center justify-center p-4 overflow-hidden relative">
            <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="relative z-10 w-full max-w-lg"
            >
                <Card className="border-red-200/30 bg-card/70 backdrop-blur-md shadow-2xl shadow-red-100/20">
                    <CardContent className="p-10 text-center">
                        {/* Icon — CSS float animation, no infinite framer loops */}
                        <motion.div variants={itemVariants} className="mb-6">
                            <div className="h-20 w-20 rounded-full bg-gradient-to-br from-red-100 to-amber-100 dark:from-red-950/50 dark:to-amber-950/50 flex items-center justify-center mx-auto shadow-lg animate-float">
                                <AlertTriangle className="h-10 w-10 text-red-500 dark:text-red-400" />
                            </div>
                        </motion.div>

                        <motion.h1
                            variants={itemVariants}
                            className="text-2xl font-bold mb-3"
                        >
                            Something Went Wrong
                        </motion.h1>

                        <motion.p
                            variants={itemVariants}
                            className="text-muted-foreground mb-2"
                        >
                            An unexpected error occurred while processing your request.
                            Don't worry — your funds and tokens are safe.
                        </motion.p>

                        {error.digest && (
                            <motion.p
                                variants={itemVariants}
                                className="text-xs text-muted-foreground/60 mb-6 font-mono"
                            >
                                Error ID: {error.digest}
                            </motion.p>
                        )}

                        {!error.digest && (
                            <motion.div variants={itemVariants} className="mb-6" />
                        )}

                        {/* Action buttons — CSS transitions instead of whileHover/whileTap */}
                        <motion.div
                            variants={itemVariants}
                            className="flex flex-col sm:flex-row gap-3 mb-6"
                        >
                            <Button
                                onClick={reset}
                                className="flex-1 bg-gradient-to-r from-red-500 to-amber-500 hover:from-red-600 hover:to-amber-600 text-white shadow-lg shadow-red-200/30 dark:shadow-red-900/30 transition-transform active:scale-95"
                            >
                                <RefreshCcw className="h-4 w-4 mr-2" />
                                Try Again
                            </Button>

                            <Button
                                variant="outline"
                                onClick={() => window.history.back()}
                                className="flex-1 border-red-200/30 dark:border-red-800/30 hover:bg-red-50 dark:hover:bg-red-950/20 transition-transform active:scale-95"
                            >
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Go Back
                            </Button>
                        </motion.div>

                        {/* Home link */}
                        <motion.div
                            variants={itemVariants}
                            className="flex items-center justify-center gap-2 text-sm text-muted-foreground"
                        >
                            <Home className="h-4 w-4" />
                            <span>Or return to the</span>
                            <Link
                                href="/"
                                className="text-primary hover:text-primary/80 underline underline-offset-2 font-medium"
                            >
                                homepage
                            </Link>
                        </motion.div>

                        <motion.div
                            variants={itemVariants}
                            className="mt-8 pt-6 border-t border-border/50"
                        >
                            <p className="text-xs text-muted-foreground/60">
                                TokenKickstarter — Your funds are always safe on the blockchain
                            </p>
                        </motion.div>
                    </CardContent>
                </Card>
            </motion.div>
        </div>
    )
}
